import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { markdownToHtml, htmlToMarkdown } from '../utils/markdownConverter';

interface DocumentContextType {
  content: string;
  updateContent: (newContent: string) => void;
  markdownContent: string;
  updateMarkdownContent: (markdown: string) => void;
  title: string;
  updateTitle: (newTitle: string) => void;
}

const DocumentContext = createContext<DocumentContextType>({
  content: '',
  updateContent: () => {},
  markdownContent: '',
  updateMarkdownContent: () => {},
  title: 'Untitled Document',
  updateTitle: () => {},
});

export const useDocumentContext = () => useContext(DocumentContext);

interface DocumentProviderProps {
  children: ReactNode;
}

// Extend Window interface
declare global {
  interface Window {
    dispatchDocumentUpdate?: (markdown: string, title?: string) => void;
  }
}

/**
 * Simple utility to detect if content is likely markdown
 */
function isMarkdownLike(text: string): boolean {
  if (!text) return false;

  const markdownPatterns = [
    /^#{1,6}\s+.+$/m, // Headers
    /\*\*[^*]+\*\*/, // Bold
    /\*[^*]+\*/, // Italic
    /^[-*]\s+.+$/m, // Unordered lists
    /^\d+\.\s+.+$/m, // Ordered lists
    /\[.+\]\(.+\)/, // Links
    /!\[.+\]\(.+\)/, // Images
    /^>\s+.+$/m, // Blockquotes
    /`{1,3}/, // Code blocks
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  children,
}) => {
  const [content, setContent] = useState<string>('');
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [title, setTitle] = useState<string>('Untitled Document');

  // Initialize global document updater - SIMPLIFIED
  useEffect(() => {
    window.dispatchDocumentUpdate = (markdown: string, docTitle?: string) => {
      if (docTitle) {
        setTitle(docTitle);
      }

      if (markdown) {
        updateMarkdownContent(markdown);
      }

      // Create and dispatch a custom event
      const event = new CustomEvent('document_update', {
        detail: { markdown, title: docTitle },
      });

      window.dispatchEvent(event);
      console.log('📄 Document update dispatched');
    };

    return () => {
      delete window.dispatchDocumentUpdate;
    };
  }, []);

  // Simple content update - NO complex processing
  const updateContent = useCallback((newContent: string) => {
    if (!newContent) {
      setContent('');
      setMarkdownContent('');
      return;
    }

    setContent(newContent);

    // Only convert to markdown if it looks like the content is markdown-like
    if (isMarkdownLike(newContent)) {
      console.log('📝 Content detected as markdown, converting to HTML');
      try {
        const htmlContent = markdownToHtml(newContent);
        setMarkdownContent(newContent);
        setContent(htmlContent);
      } catch (error) {
        console.error('Error converting markdown to HTML:', error);
      }
    }
  }, []);

  // Simple markdown update - NO complex processing
  const updateMarkdownContent = useCallback(
    (markdown: string) => {
      if (!markdown) {
        setMarkdownContent('');
        setContent('');
        return;
      }

      setMarkdownContent(markdown);

      // Convert to HTML for display
      try {
        const html = markdownToHtml(markdown);
        setContent(html);

        // Dispatch document update event
        const event = new CustomEvent('document_update', {
          detail: { markdown, title },
        });
        window.dispatchEvent(event);
        console.log(
          '📄 Document update event dispatched after markdown update'
        );
      } catch (error) {
        console.error('Error converting markdown to HTML:', error);
        setContent(markdown); // Fallback to raw markdown
      }
    },
    [title]
  );

  const updateTitle = useCallback((newTitle: string) => {
    setTitle(newTitle || 'Untitled Document');
  }, []);

  return (
    <DocumentContext.Provider
      value={{
        content,
        updateContent,
        markdownContent,
        updateMarkdownContent,
        title,
        updateTitle,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
};
